import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import QRCode from 'qrcode';
import { toast } from 'react-toastify';
import { Modal, Button } from 'react-bootstrap';
import { PDFViewer, pdf } from '@react-pdf/renderer';
import { Icon } from '@iconify/react/dist/iconify.js';
import StatusComplaintsReport from '../../reports/StatusComplaintsReport';
import { statusLabel } from '../../utils/displayText';

// Generic version of EscalatedReportModal.jsx — opens the PDF report for any
// complaint status (Open, Assigned, Closed, Re-Open, Dropped, Resolved) card
// on the dashboard. Same lifecycle: fetch the grouped data, build a QR code,
// render the PDF in a viewer, and offer a download.
const REPORT_PATH = '/reports/status-complaints';
const REQUEST_TIMEOUT_MS = 60000;

const fileDateStamp = (date) => {
  const d = date ? new Date(date) : new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()}`;
};

// title: display label (e.g. "Assigned", "Re-Open")
// apiStatus: raw backend status value (e.g. "In Progress", "Reopened")
const StatusReportModal = ({ show, onHide, title = '', apiStatus }) => {
  // "Closed" is shown as "Reply Submitted" (display only; apiStatus stays "Closed").
  const displayTitle = statusLabel(title);
  const [report, setReport] = useState(null);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const isMounted = useRef(true);
  const API_BASE_URL = process.env.REACT_APP_BE_API_URL;
  const APP_BASE_URL = process.env.REACT_APP_URL;

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const fetchReport = useCallback(async () => {
    if (!apiStatus) return;
    setIsLoading(true);
    setError(null);
    setReport(null);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Your session has expired. Please sign in again.');
        return;
      }

      const response = await axios.get(
        `${API_BASE_URL}/backend/complaints/complaints-report-by-status`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { status: apiStatus },
          timeout: REQUEST_TIMEOUT_MS,
        }
      );

      if (!isMounted.current) return;

      if (!response.data?.status) {
        throw new Error(response.data?.message || 'The server returned an unexpected response.');
      }

      let qr = '';
      try {
        qr = await QRCode.toDataURL(`${APP_BASE_URL || ''}${REPORT_PATH}`, { width: 120 });
      } catch (qrErr) {
        console.warn('QR generation failed, continuing without it:', qrErr);
      }

      if (!isMounted.current) return;

      setQrCodeUrl(qr);
      setReport({
        data: response.data.data || [],
        generatedAt: response.data.generatedAt,
        totalComplaints: response.data.totalComplaints || 0,
        totalOfficers: response.data.totalOfficers || 0,
      });
    } catch (err) {
      if (!isMounted.current) return;
      if (err.code === 'ECONNABORTED') {
        setError(`The request took longer than ${REQUEST_TIMEOUT_MS / 1000} seconds and was cancelled. Please try again.`);
      } else if (err.response?.status === 404) {
        setError('The complaints report endpoint was not found on the server (404).');
      } else if (err.response && (err.response.status === 401 || err.response.status === 403)) {
        setError('You do not have permission to view this report.');
      } else {
        setError(err.message || 'An unexpected error occurred.');
      }
      console.error('Error fetching complaints report:', err);
    } finally {
      if (isMounted.current) setIsLoading(false);
    }
  }, [API_BASE_URL, APP_BASE_URL, apiStatus]);

  useEffect(() => {
    if (show) {
      fetchReport();
    } else {
      setReport(null);
      setError(null);
      setIsLoading(true);
    }
  }, [show, fetchReport]);

  const handleDownload = useCallback(async () => {
    if (!report) return;
    setIsDownloading(true);
    try {
      const blob = await pdf(
        <StatusComplaintsReport
          title={title}
          apiStatus={apiStatus}
          reportData={report.data}
          generatedAt={report.generatedAt}
          totalComplaints={report.totalComplaints}
          totalOfficers={report.totalOfficers}
          qrCodeUrl={qrCodeUrl}
        />
      ).toBlob();

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${title}-Complaints-List-${fileDateStamp(report.generatedAt)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success('Report downloaded successfully.');
    } catch (err) {
      console.error('Error generating PDF:', err);
      toast.error('Failed to download the report.');
    } finally {
      if (isMounted.current) setIsDownloading(false);
    }
  }, [report, qrCodeUrl, title, apiStatus]);

  return (
    <Modal show={show} onHide={onHide} centered size="xl" fullscreen="lg-down">
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center gap-2">
          <Icon icon="mdi:file-chart-outline" className="text-success" />
          {displayTitle} Complaints Report
          {report && (
            <span className="badge bg-success-focus text-success-600 ms-2" style={{ fontSize: '0.7rem' }}>
              {report.totalComplaints} complaint{report.totalComplaints === 1 ? '' : 's'} · {report.totalOfficers} officer{report.totalOfficers === 1 ? '' : 's'}
            </span>
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ height: '75vh', padding: 0 }}>
        {isLoading ? (
          <div className="d-flex flex-column align-items-center justify-content-center h-100 gap-3 text-secondary">
            <Icon icon="eos-icons:loading" width="42" height="42" className="text-success" />
            <span>Building the {displayTitle.toLowerCase()} complaints report…</span>
          </div>
        ) : error ? (
          <div className="d-flex flex-column align-items-center justify-content-center h-100 gap-3 text-center px-4">
            <Icon icon="material-symbols:error-outline" width="42" height="42" color="#dc3545" />
            <h6 className="text-danger mb-0">Could not load the report</h6>
            <p className="text-secondary mb-0" style={{ maxWidth: 480 }}>{error}</p>
            <Button variant="outline-secondary" size="sm" onClick={fetchReport}>
              <Icon icon="mdi:refresh" className="me-1" /> Try again
            </Button>
          </div>
        ) : report ? (
          <PDFViewer width="100%" height="100%" showToolbar style={{ display: 'block', border: 'none' }}>
            <StatusComplaintsReport
              title={title}
              apiStatus={apiStatus}
              reportData={report.data}
              generatedAt={report.generatedAt}
              totalComplaints={report.totalComplaints}
              totalOfficers={report.totalOfficers}
              qrCodeUrl={qrCodeUrl}
            />
          </PDFViewer>
        ) : (
          <div className="d-flex align-items-center justify-content-center h-100 text-secondary">
            No {displayTitle.toLowerCase()} complaints data is available right now.
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="success" onClick={handleDownload} disabled={!report || isDownloading}>
          <Icon icon={isDownloading ? 'eos-icons:loading' : 'mdi:download'} className="me-1" />
          {isDownloading ? 'Preparing…' : 'Download PDF'}
        </Button>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default StatusReportModal;
